import { useCallback, useState, type MutableRefObject } from 'react'
import { flowRuleStateMeta } from './flow-rule-state'
import type { HydraulicTreeRow } from './types'

// "Errors and/or warnings displayed should disappear within about 20 seconds"
// (Controller UI v230 §3.3.6), so the outcome is not known until roughly then.
// Re-checked a few times across that window rather than once at the end, so a
// quick clear reports quickly.
const VERIFY_STEPS_MS = [2000, 4000, 6000, 8000, 10000, 15000]

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

interface UseErrorClearingArgs {
  rowDataRef: MutableRefObject<HydraulicTreeRow[]>
  onRefetch?: () => Promise<any>
}

/**
 * Tracks what happened to a "clear error" click.
 *
 * Clearing an alarm is a request, not a result: the rule's state is recomputed
 * continuously, so if the cause is still present the same error returns within
 * seconds. Without this the row silently goes back to red and the button looks
 * broken — which is exactly how it read before.
 */
const useErrorClearing = ({ rowDataRef, onRefetch }: UseErrorClearingArgs) => {
  const [clearingIds, setClearingIds] = useState<ReadonlySet<number>>(new Set())
  const [returnedIds, setReturnedIds] = useState<ReadonlySet<number>>(new Set())

  const mark = (
    setter: (next: (prev: ReadonlySet<number>) => ReadonlySet<number>) => void,
    id: number,
    on: boolean,
  ) =>
    setter((prev) => {
      if (prev.has(id) === on) return prev
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const stillFaulted = (pipeId: number) => {
    const row = rowDataRef.current.find((entry) => entry.id === pipeId)
    const kind = flowRuleStateMeta(row?.status).kind
    return kind === 'alarm' || kind === 'config'
  }

  const clearErrors = useCallback(
    async (pipeId: number, run: () => Promise<unknown>) => {
      mark(setClearingIds, pipeId, true)
      mark(setReturnedIds, pipeId, false)

      try {
        await run()
      } catch {
        // The mutation reports its own failure; this is only the row's badge.
        mark(setClearingIds, pipeId, false)
        return
      }

      for (const step of VERIFY_STEPS_MS) {
        await sleep(step)
        await onRefetch?.()
        // One more tick so the refetched rows have reached rowDataRef.
        await sleep(0)
        if (!stillFaulted(pipeId)) {
          mark(setClearingIds, pipeId, false)
          return
        }
      }

      mark(setClearingIds, pipeId, false)
      mark(setReturnedIds, pipeId, true)
    },
    // `stillFaulted` reads through refs, so it needs no dependency of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRefetch],
  )

  /** Drops the "came back" badge — the user has seen it. */
  const dismissReturned = useCallback(
    (pipeId: number) => mark(setReturnedIds, pipeId, false),
    [],
  )

  return { clearingIds, returnedIds, clearErrors, dismissReturned }
}

export default useErrorClearing
